import { z } from "zod";

import {
  SCRAPE_CONTENT_TYPES,
  SCRAPE_CONTENT_TYPE_OPTIONS,
  CONTENT_TYPE_LABEL as SHARED_CONTENT_TYPE_LABEL,
  type ScrapeContentType,
} from "@/lib/tina/content-types";

export const PRESETS_STORAGE_KEY = "ims.tina.runSitePresets.v2";
export const UNKNOWN_CITY = "Unspecified";
export const UNKNOWN_COUNTRY = "ZZ";

export type TinaSiteOption = {
  name: string;
  content_type: string;
  x_strategy: string;
  country_code: string;
  city: string | null;
  enabled: boolean;
  last_processed_at?: string | null;
  cadence_minutes?: number | null;
  source?: string;
  config?: Record<string, unknown> | null;
};

export type SourceKind = "websites" | "instagram" | "facebook";

/**
 * Content types re-exported from the single vocabulary.
 *
 * Deliberately the **scrape-only** subset: this module's three consumers — the
 * sites list toolbar, run create, and the group site picker — pick a value that
 * must be legal on a `sites` row, and the nine `enrichment_*` types never are.
 */
export type ContentType = ScrapeContentType;

export const CONTENT_TYPES = SCRAPE_CONTENT_TYPE_OPTIONS;

export const CONTENT_TYPE_LABEL: Record<ContentType, string> =
  SHARED_CONTENT_TYPE_LABEL;

export const SOURCE_KINDS: ReadonlyArray<{ value: SourceKind; label: string }> =
  [
    { value: "websites", label: "Websites" },
    { value: "instagram", label: "Instagram" },
    { value: "facebook", label: "Facebook" },
  ];

export const SOURCE_LABEL: Record<SourceKind, string> = {
  websites: "Websites",
  instagram: "Instagram",
  facebook: "Facebook",
};

export function normalizeCity(city: string | null | undefined) {
  const trimmed = city?.trim();
  return trimmed || UNKNOWN_CITY;
}

/**
 * ISO 3166-1 alpha-2 country code → flag emoji (regional indicator symbols).
 * Returns "" for the unknown sentinel or any non-alpha-2 value so callers can
 * fall back to the bare code.
 */
export function countryFlag(code: string): string {
  const upper = code.toUpperCase();
  if (upper === UNKNOWN_COUNTRY || !/^[A-Z]{2}$/.test(upper)) return "";
  const base = 0x1f1e6; // regional indicator "A"
  return String.fromCodePoint(
    base + (upper.charCodeAt(0) - 65),
    base + (upper.charCodeAt(1) - 65),
  );
}

/** Read known_venue_id from a site option's config (null if absent/blank). */
export function extractKnownVenueIdFromOption(
  site: TinaSiteOption,
): string | null {
  const value = site.config?.["known_venue_id"];
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function compareSites(left: TinaSiteOption, right: TinaSiteOption) {
  return (
    normalizeCity(left.city).localeCompare(normalizeCity(right.city)) ||
    left.name.localeCompare(right.name)
  );
}

export function sortSites(sites: TinaSiteOption[]) {
  return [...sites].sort(compareSites);
}

/** Identity key for one (content × source × country) selection bucket. */
export function comboKey(
  contentType: ContentType,
  source: SourceKind,
  country: string | null,
) {
  return `${contentType}|${source}|${country ?? ""}`;
}

export interface RunPreset {
  id: string;
  name: string;
  contentType: ContentType;
  source: SourceKind;
  country: string;
  sites: string[];
  createdAt: string;
}

const runPresetSchema = z.object({
  id: z.string(),
  name: z.string(),
  contentType: z.enum(SCRAPE_CONTENT_TYPES),
  source: z.enum(["websites", "instagram", "facebook"]),
  country: z.string(),
  sites: z.array(z.string()),
  createdAt: z.string(),
});

const runPresetsSchema = z.array(runPresetSchema);

export function loadPresets(): RunPreset[] {
  if (typeof window === "undefined") return [];
  try {
    const value = window.localStorage.getItem(PRESETS_STORAGE_KEY);
    if (!value) return [];
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];

    const presets: RunPreset[] = [];
    for (const entry of parsed) {
      const result = runPresetSchema.safeParse(entry);
      if (result.success) {
        presets.push(result.data);
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          "[run-source-picker] dropping malformed preset entry",
          result.error.issues,
        );
      }
    }
    return presets;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[run-source-picker] failed to parse presets storage", error);
    return [];
  }
}

export function persistPresets(presets: RunPreset[]) {
  if (typeof window === "undefined") return;
  const validated = runPresetsSchema.safeParse(presets);
  const toWrite = validated.success ? validated.data : presets;
  window.localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(toWrite));
}

export function createPresetId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
